import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type PosPriceAdjustmentShortcut,
  sortByDisplayIndex,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { ArrowUpDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ActionDropdown } from '@/components/ActionDropdown';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePosConfig } from '@/routes/restaurant/useRestaurantData';
import { PriceAdjustmentShortcutForm } from './PriceAdjustmentShortcutForm';
import { PriceAdjustmentShortcutsRearrangeList } from './PriceAdjustmentShortcutsRearrangeList';

export const Route = createFileRoute('/r/$restaurantId/s/pos-price-adjustments')({
  component: PosPriceAdjustmentsPage,
});

function PosPriceAdjustmentsPage() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const posConfig = usePosConfig();
  const [formState, setFormState] = useState<PosPriceAdjustmentShortcut | 'new' | 'closed'>(
    'closed',
  );
  const [isRearranging, setIsRearranging] = useState(false);

  const handleDelete = async (posPriceAdjustmentShortcutUuid: string) => {
    try {
      await convex.mutation(api.posConfigs.deletePosPriceAdjustmentShortcut, {
        posPriceAdjustmentShortcutUuid,
        restaurantId,
      });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  if (!posConfig) return <div>No config yet.</div>;

  // Listed in the order their buttons show on the POS, which the admin sets by rearranging them
  const posPriceAdjustmentShortcuts = sortByDisplayIndex(posConfig.posPriceAdjustmentShortcuts);

  const formatAmount = (shortcut: PosPriceAdjustmentShortcut): string =>
    `${shortcut.percent}% ${shortcut.type === 'DISCOUNT' ? 'off' : 'extra'}`;

  return (
    <>
      {formState !== 'closed' && (
        <PriceAdjustmentShortcutForm
          editShortcut={formState === 'new' ? undefined : formState}
          onClose={() => setFormState('closed')}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Price adjustment shortcuts</CardTitle>
          <CardDescription>
            One-tap discounts and surcharges staff can apply on the POS, like a staff discount or
            Sunday surcharge
          </CardDescription>
          {!isRearranging && (
            <CardAction>
              <ActionButton onClick={() => setFormState('new')}>Create New</ActionButton>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {isRearranging ? (
            <PriceAdjustmentShortcutsRearrangeList
              onRearrangingDone={() => setIsRearranging(false)}
              posPriceAdjustmentShortcuts={posPriceAdjustmentShortcuts}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reason</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead className="w-[64px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posPriceAdjustmentShortcuts.map((shortcut) => (
                    <TableRow key={shortcut.uuid}>
                      <TableCell className="font-mono">
                        {shortcut.reason ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>{formatAmount(shortcut)}</TableCell>
                      <TableCell>
                        <ActionDropdown
                          onDelete={() => handleDelete(shortcut.uuid)}
                          onEdit={() => setTimeout(() => setFormState(shortcut), 0)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {posPriceAdjustmentShortcuts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground">
                        No shortcuts set up yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Sits under the rows it reorders, so it reads as an action on the list */}
              <div className="mt-4">
                <ActionButton
                  disabled={posPriceAdjustmentShortcuts.length <= 1}
                  disabledTooltipPosition={'top'}
                  disabledTooltipText={'You need more shortcuts'}
                  onClick={() => setIsRearranging(true)}
                  variant={'outline'}
                >
                  <ArrowUpDown />
                  Rearrange
                </ActionButton>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
